import React, { useEffect, useRef, useState } from 'react';
import { IKpiVessel, TPolygonCalls, TPolygonCallsItem } from '../../../kpiTypes';
import { AgGridReact } from 'ag-grid-react';
import { themeBalham } from 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-community';
import MapLibre from '../../../../../shared/components/maps/map-libre/MapLibre';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import { formatValueAsString } from '../../../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../../core/store';
import { getVesselLastPortCallsRequestAction } from '../../../../main-map/slices/vesselLastPortCallsSlice';
import { Layer, Source } from 'react-map-gl';
import { portCallsLayerStyle } from '../../../../../shared/components/maps/map-libre/mapLayers';
import PortCallsMapLegend from './PortCallsMapLegend';
import { TVesselInfo } from '../../../../main-map/mapTypes';
import PortCallsChartApex from './PortCallsChartApex';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';
import { findCenterPoint } from '../../../../../utils/mapHelpers';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../../date-range/slices/dateRangeSlice';
export interface IPortCallsProps {
	vessel: IKpiVessel;
	vesselPolygonCalls: TPolygonCalls | null;
}

const PortCalls: React.FC<IPortCallsProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();
	const { vesselLastPortCallsData } = useAppSelector((state: RootState) => state.vesselLastPortCallsSlice);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	const [portCalls, setPortCalls] = useState<TVesselInfo>();
	const [filteredPortCalls, setFilteredPortCalls] = useState<TVesselInfo>();

	const numberFormatOptions: Intl.NumberFormatOptions = { minimumFractionDigits: 0, maximumFractionDigits: 2 };
	const gridColDefs: ColDef<TPolygonCallsItem>[] = [
		{
			headerName: 'Port Call Id',
			field: 'id',
		},
		{
			headerName: 'Start Time',
			field: 'startTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{
			headerName: 'End Time',
			field: 'endTime',
			valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : ''),
		},
		{ headerName: 'Port', field: 'portName' },
		{ headerName: 'Port Activity', field: 'portActivity' },
		{
			headerName: 'Total Time In Port',
			field: 'totalTimeInPort',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Time At Anchor',
			field: 'timeAtAnchor',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Time Moored',
			field: 'timeMoored',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Time Maneouvring',
			field: 'timeManeouvring',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Time Unclassified',
			field: 'timeUnclassified',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Draught In',
			field: 'draughtIn',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Draught Out',
			field: 'draughtOut',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Deadweight Loaded On Arrival',
			field: 'deadweightLoadedOnArrival',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
		{
			headerName: 'Deadweight Loaded On Departure',
			field: 'deadweightLoadedOnDeparture',
			valueFormatter: (params) => formatValueAsString(params.value, numberFormatOptions),
		},
	];

	const rowData = props.vesselPolygonCalls && props.vesselPolygonCalls.dataList ? props.vesselPolygonCalls.dataList : [];

	useEffect(() => {
		if (props.vessel) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();
			dispatch(getVesselLastPortCallsRequestAction({ imoNumber: props.vessel.imoNumber, startDate, endDate }));
		}
	}, [dispatch, props.vessel, analysisDateRange]);

	useEffect(() => {
		if (vesselLastPortCallsData && vesselLastPortCallsData.features && vesselLastPortCallsData.features.length > 0) {
			const dischargeLoadUnknownPortCalls = vesselLastPortCallsData?.features.filter(
				(item) =>
					item.properties?.portActivity === 'Discharge' ||
					item.properties?.portActivity === 'Load' ||
					!item.properties?.portActivity,
			);
			if (dischargeLoadUnknownPortCalls && dischargeLoadUnknownPortCalls.length > 0) {
				const filteredPortCalls: TVesselInfo = {
					type: 'FeatureCollection',
					features: dischargeLoadUnknownPortCalls,
				};
				setPortCalls(filteredPortCalls);
				setFilteredPortCalls(filteredPortCalls);
			}
		}
	}, [dispatch, vesselLastPortCallsData]);

	const onPortCallsMapLegendSelectionChange = (key: string) => {
		if (!key) {
			setFilteredPortCalls(portCalls);
			return;
		}

		if (portCalls && portCalls.features && portCalls.features.length > 0) {
			const filteredPortCalls = {
				type: 'FeatureCollection',
				features: portCalls.features.filter(
					(item) => (key === 'other' && !item.properties?.portActivity) || item.properties?.portActivity === key,
				),
			};
			setFilteredPortCalls(filteredPortCalls);
		}
	};

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-col gap-4 w-full">
			<div className="flex flex-row h-1/2 gap-4">
				<div className="flex flex-col basis-1/3 border border-msi-darkgrey-color-300 rounded p-1">
					<PortCallsMapLegend onSelectionChange={onPortCallsMapLegendSelectionChange} />
					{filteredPortCalls && (
						<MapLibre center={findCenterPoint(filteredPortCalls)} loadChildrenDirectly>
							<Source id="portCalls" type="geojson" data={filteredPortCalls}>
								<Layer {...portCallsLayerStyle()} type="circle" />
							</Source>
						</MapLibre>
					)}
				</div>
				<div className="flex flex-col basis-2/3 border border-msi-darkgrey-color-300 rounded">
					{!props.vesselPolygonCalls|| props.vesselPolygonCalls?.loading ? (
						<LoadingSpinner size="md" spinnerClassName="p-2" />
					) : (
						<ExpandableContainer panelOffsets={panelOffset}>
							<PortCallsChartApex data={props.vesselPolygonCalls?.dataList || []} />
						</ExpandableContainer>
					)}
				</div>
			</div>
			<div className="flex flex-col gap-4 h-1/2">
				<div className="w-full h-full border border-msi-darkgrey-color-300 rounded p-1">
					<ExpandableContainer panelOffsets={panelOffset}>
						<AgGridReact
							key={props.vessel.imoNumber}
							columnDefs={gridColDefs}
							rowData={rowData}
							className="w-full h-full"
							theme={themeBalham}
						/>
					</ExpandableContainer>
				</div>
			</div>
		</div>
	);
};

export default PortCalls;
